import { useState, useEffect, useCallback } from 'react';
import { bskyCreateSession, bskyPost } from '../utils';
import { exportItemsToBufferCsv } from '../utils/exportBufferCsv';
import AssetUrlEditor from './AssetUrlEditor';
import YoutubeQueue from './YoutubeQueue';
import PlatformMockup from './PlatformMockup';

const BACKEND    = 'https://strategic-honesty-media-studio-production.up.railway.app';
const API_URL    = process.env.REACT_APP_MEDIA_STUDIO_API_URL || 'https://strategic-honesty-media-studio-production.up.railway.app';
const API_KEY    = process.env.REACT_APP_API_KEY;

const authFetch = (url, opts = {}) =>
  fetch(url, {
    ...opts,
    headers: { 'Content-Type': 'application/json', 'x-api-key': API_KEY, ...(opts.headers || {}) },
  });

/**
 * Exports the given items to a Buffer CSV for the named platform,
 * then stamps lastUsedAt on each exported item so the content
 * rotation system (Phase B/C) has data to work with from day one.
 */
function exportPinterestCsv(items, setToastFn) {
  const imageItems = items.filter(item => {
    const url = item.outputUrl || item.imageUrl || '';
    return url.startsWith('https://') && !url.endsWith('.mp4') && !url.endsWith('.mov');
  });
  if (!imageItems.length) {
    setToastFn({ msg: 'No image items with R2 URLs found', type: 'error' });
    return;
  }
  const rows = [
    ['Title','Media URL','Pinterest board','Thumbnail','Description','Link','Publish date','Keywords']
  ];
  const base = new Date();
  imageItems.forEach((item, i) => {
    const publishDate = new Date(base.getTime() + i * 24 * 60 * 60 * 1000);
    const dateStr = publishDate.toISOString().slice(0, 19);
    const title = (item.title || 'Strategic Honesty').slice(0, 100);
    const desc = (item.sourceText || item.title || 'Be Good. Do Good. Do Well. — Gopu Shrestha | strategichonesty.com').replace(/\n/g, ' ').slice(0, 500);
    const url = item.outputUrl || item.imageUrl || '';
    rows.push([title, url, 'Strategic Honesty', '', desc, 'https://strategichonesty.com', dateStr, 'integrity, leadership, honesty, strategic honesty, gopu shrestha']);
  });
  const csv = rows.map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(',')).join('\r\n');
  const blob = new Blob([csv], { type: 'text/csv' });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = `pinterest_bulk_${new Date().toISOString().slice(0,10)}.csv`;
  a.click();
  URL.revokeObjectURL(a.href);
  setToastFn({ msg: `📌 Pinterest CSV: ${imageItems.length} pins exported`, type: 'success' });
}

async function exportToBuffer(items, platformLabel, setToastFn) {
  if (!items.length) {
    setToastFn({ message: 'No completed items to export', type: 'error' });
    return;
  }
  const filename = exportItemsToBufferCsv(items, platformLabel);

  const today = new Date().toISOString();
  await Promise.all(
    items.map(item =>
      authFetch(`${API_URL}/api/queue/${item.id}`, {
        method: 'PATCH',
        body: JSON.stringify({ lastUsedAt: today }),
      }).catch(e => console.warn('[exportToBuffer] lastUsedAt stamp failed for', item.id, e.message))
    )
  );

  setToastFn({ message: `Exported ${items.length} item(s) to ${filename}`, type: 'success' });
}

const isVideo       = (u) => typeof u === 'string' && (/\.(mp4|mov|webm)/i.test(u) || u.startsWith('data:video'));
const isBase64Image = (u) => typeof u === 'string' && u.startsWith('data:image');
const isHttpImage   = (u) => typeof u === 'string' && /^https?:\/\//i.test(u) && !isVideo(u) && !u.startsWith('data:');

export default function ReviewPublish({ onCountChange }) {
  const [items,      setItems]      = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [activeIdx,  setActiveIdx]  = useState(0);
  const [publishing, setPublishing] = useState(false);
  const [toast,      setToast]      = useState(null);
  const [platforms,  setPlatforms]  = useState({ bluesky: true, mastodon: true, facebook: false, linkedin: true, tiktok: false, pinterest: false });
  const [editedContent, setEditedContent] = useState('');
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [generatingCaption, setGeneratingCaption] = useState(false);
  const [generatingAll, setGeneratingAll] = useState(false);
  const [generateAllProgress, setGenerateAllProgress] = useState('');
  const [scheduleStartDate, setScheduleStartDate] = useState('');
  const [scheduleInterval, setScheduleInterval] = useState(1);
  const [scheduleUnit, setScheduleUnit] = useState('days');
  const [scheduling, setScheduling] = useState(false);
  const [scheduleLog, setScheduleLog] = useState([]);
  const [bufferCounts, setBufferCounts] = useState({});
  const [openPlatItems, setOpenPlatItems] = useState({});
  const [bufferPosts, setBufferPosts] = useState([]);
  const [expandedPlatform, setExpandedPlatform] = useState(null);
  const [removingId, setRemovingId] = useState(null);

  // Fetch buffer queue counts from backend
  const fetchBufferCounts = async () => {
    try {
      const res = await authFetch(`${API_URL}/api/buffer-queue`);
      const data = await res.json();
      if (data.success) {
        setBufferCounts(data.counts || {});
        setBufferPosts(data.posts || []);
      }
    } catch (e) {
      console.warn('[bufferCounts]', e.message);
    }
  };

  // Remove a single queued item; re-syncs count + list together (Lesson #1)
  const handleRemoveQueueItem = async (id) => {
    setRemovingId(id);
    try {
      const res = await authFetch(`${API_URL}/api/buffer-queue/${id}`, { method: 'DELETE' });
      const data = await res.json();
      if (data.success) {
        await fetchBufferCounts();
      } else {
        showToast(data.error || 'Failed to remove item', 'error');
      }
    } catch (e) {
      showToast('Failed to remove item', 'error');
    } finally {
      setRemovingId(null);
    }
  };

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 4000);
  };

  // ── Poll every 30s ──────────────────────────────────────────────────────────
  const fetchCompleted = useCallback(async () => {
    try {
      // "Review & Publish" is meant to show finished content awaiting human
      // approval — status transitions to 'completed' once media generation
      // succeeds, then to 'published' once approved here. Fetching
      // status=pending was backwards: it only ever showed items whose media
      // generation hadn't finished (or was stuck), and made genuinely
      // finished items disappear the moment they became reviewable.
      const res = await authFetch(`${API_URL}/api/queue?status=completed`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      const all = Array.isArray(data) ? data : (Array.isArray(data?.items) ? data.items : []);
      // Items made by the idea pipeline are reviewed and approved on the main Review & Publish
      // screen (one approval for all platforms). Listing them here too would show everything
      // twice and let one be approved outside that flow.
      const list = all.filter((i) => i.source !== 'pipeline');
      setItems(list);
      if (onCountChange) onCountChange(list.length);
    } catch (err) {
      console.error('[ReviewPublish] fetch:', err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCompleted();
    fetchBufferCounts();
    const iv = setInterval(() => {
      fetchCompleted();
      fetchBufferCounts();
    }, 30000);
    return () => clearInterval(iv);
  }, [fetchCompleted]);

  const current = items[activeIdx] || null;

  useEffect(() => {
    setEditedContent(current?.sourceText || '');
    if (current) setTimeout(() => handleGenerateCaption(), 400);
  }, [current]);

  // Auto-select platform toggles to match what Media Studio prepared the content for
  useEffect(() => {
    if (!current?.platform) return;
    const p = current.platform.toLowerCase();
    setPlatforms({
      bluesky:           p === 'bluesky',
      mastodon:          p === 'mastodon',
      twitter:           p === 'twitter' || p === 'x' || p === 'x/twitter' || p === 'x_twitter' || p === 'x / twitter',
      threads:           p === 'threads',
      facebook:          p === 'facebook',
      linkedin:          p === 'linkedin',
      tiktok:            p === 'tiktok',
      pinterest:         p === 'pinterest',
      'youtube shorts':  p === 'youtube shorts' || p === 'yt_short' || p === 'youtube_shorts',
      'facebook reels':  p === 'facebook reels' || p === 'fb_reels',
      'instagram reels': p === 'instagram reels' || p === 'ig_reels',
      instagram:         p === 'instagram',
    });
  }, [current]);

  // ── Export CSV from buffer_queue (Facebook/LinkedIn/TikTok/Pinterest) ────────
  const handleExportCsv = async (platform, label) => {
    try {
      const res = await authFetch(`${API_URL}/api/buffer-queue/export`, {
        method: 'DELETE',
        body: JSON.stringify({ platform }),
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.error || 'Export failed');

      // Fetch scheduled_at map for this platform so CSV Posting Time matches Calendar
      let postingTimesMap = {};
      try {
        const ptRes = await authFetch(`${API_URL}/api/scheduled-calendar/posting-times?platform=${platform}`);
        const ptData = await ptRes.json();
        postingTimesMap = ptData.times || {};
      } catch (e) {
        console.warn('[posting-times] fetch failed, falling back to sequential dates', e.message);
      }

      // Build CSV
      const isPinterest = platform === 'pinterest';
      const rows = isPinterest
        ? [['Title', 'Media URL', 'Pinterest board', 'Thumbnail', 'Description', 'Link', 'Publish date', 'Keywords']]
        : [['Date', 'Message', 'Media URL']];

      const now = new Date();
      data.posts.forEach((post, i) => {
        // Use scheduled_at from scheduled_posts if available, else fall back to sequential dates
        const scheduledMs = post.idea_id && postingTimesMap[post.idea_id]
          ? postingTimesMap[post.idea_id]
          : now.getTime() + (i + 1) * 24 * 60 * 60 * 1000;
        const d = new Date(scheduledMs);
        const dateStr = d.toISOString().slice(0, 10);
        const mediaUrl = post.pinterest_url || post.image_url || post.video_url || '';
        if (isPinterest) {
          const pinTitle = (post.title || 'Strategic Honesty').slice(0, 100);
          const pinDesc  = (post.content || post.title || 'Be Good. Do Good. Do Well. — Gopu Shrestha | strategichonesty.com').replace(/\n/g, ' ').slice(0, 500);
          rows.push([pinTitle, mediaUrl, 'Strategic Honesty', '', pinDesc, 'https://strategichonesty.com', dateStr, 'integrity, leadership, honesty, strategic honesty, gopu shrestha']);
        } else {
          rows.push([dateStr, post.content || '', mediaUrl]);
        }
      });

      const csv = rows.map(r => r.map(c => '"' + String(c).replace(/"/g, '""') + '"').join(',')).join('\n');
      const blob = new Blob([csv], { type: 'text/csv' });
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement('a');
      a.href     = url;
      a.download = `${platform}_buffer_${new Date().toISOString().slice(0,10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);

      showToast(`Downloaded ${data.exported} posts for ${label} ✓`);
      fetchBufferCounts();
    } catch (e) {
      showToast(`Export failed: ${e.message}`, 'error');
    }
  };

  // ── Publish Bluesky/Mastodon queue to Postiz with scheduled slots ─────────
  const handlePublishToPostiz = async (platform) => {
    try {
      // Fetch pending posts for this platform
      const res = await authFetch(`${API_URL}/api/buffer-queue`);
      const data = await res.json();
      if (!data.success) throw new Error('Could not fetch queue');

      const posts = (data.posts || []).filter(p => p.platform === platform);
      if (!posts.length) { showToast('No queued posts for ' + platform, 'error'); return; }

      // Slot definitions — kept in sync with platSlots in handleApprove() below.
      // Bluesky: Mon/Fri 9am CT. Mastodon: Tue/Sat 9am CT.
      const slots = platform === 'bluesky'
        ? [{ day: 1, hour: 14 }, { day: 5, hour: 14 }]  // UTC
        : [{ day: 2, hour: 14 }, { day: 6, hour: 14 }];  // UTC

      const INTEGRATION_ID = platform === 'bluesky'
        ? 'cmq76myko0001pj8stvtllcuy'
        : 'cmqbijehr0001o797udmsniel';

      // Find next available slot after now + 2 hours buffer.
      // (Currently unused — getNextSlotSequential below is what the actual
      // publish loop calls — but fixed to match it rather than left as a
      // landmine: this had the same "|| 7 forces same weekday onto next
      // week, skipping the platform's other slot" bug as getNextApproveSlot,
      // plus `week` never actually advanced the candidate date.)
      const getNextSlot = (afterDate) => {
        const d = new Date(afterDate.getTime() + 2 * 60 * 60 * 1000);
        for (let week = 0; week < 8; week++) {
          for (const slot of slots) {
            const candidate = new Date(d);
            candidate.setUTCDate(candidate.getUTCDate() + week * 7);
            const daysUntil = (slot.day - candidate.getUTCDay() + 7) % 7;
            candidate.setUTCDate(candidate.getUTCDate() + daysUntil);
            candidate.setUTCHours(slot.hour, 0, 0, 0);
            if (candidate > d) return candidate;
          }
        }
        return new Date(d.getTime() + 48 * 60 * 60 * 1000);
      };

      // Assign slots sequentially — start from last scheduled slot, not now
      const scheduledTimes = [];
      let lastSlotMs = null;
      try {
        const lsRes = await authFetch(`${API_URL}/api/scheduled-calendar/last-slot?platform=${platform}`);
        const lsData = await lsRes.json();
        lastSlotMs = lsData.lastSlot || null;
      } catch (e) {
        console.warn('[last-slot] fetch failed, falling back to now', e.message);
      }
      let lastSlot = lastSlotMs ? new Date(lastSlotMs) : new Date();
      for (const post of posts) {
        const slotTime = getNextSlotSequential(lastSlot, slots);
        scheduledTimes.push(slotTime);
        lastSlot = slotTime;
      }

      // Fire each post to Postiz
      let fired = 0;
      for (let i = 0; i < posts.length; i++) {
        const post = posts[i];
        const scheduledAt = scheduledTimes[i].toISOString();
        const postContent = platform === 'bluesky' && post.content.length > 280
          ? post.content.slice(0, 277) + '...'
          : post.content;
        const mediaUrl = post.pinterest_url || post.image_url || null;

        try {
          const pRes = await authFetch(`${BACKEND}/scheduler/posts/postiz/publish`, {
            method: 'POST',
            body: JSON.stringify({
              integrationId: INTEGRATION_ID,
              content: postContent,
              platformType: platform,
              type: 'schedule',
              date: scheduledAt,
              images: mediaUrl && !mediaUrl.startsWith('data:') ? [{ id: 'sh-media-1', path: mediaUrl }] : [],
            }),
          });
          const pData = await pRes.json();
          if (pData.success) fired++;
          else console.warn('[postiz]', pData.error);
        } catch (e) {
          console.warn('[postiz fire]', e.message);
        }
      }

      // Mark as exported in buffer_queue
      await authFetch(`${API_URL}/api/buffer-queue/export`, {
        method: 'DELETE',
        body: JSON.stringify({ platform }),
      });

      showToast(`Scheduled ${fired}/${posts.length} to Postiz ✓`);
      fetchBufferCounts();
    } catch (e) {
      showToast(`Postiz publish failed: ${e.message}`, 'error');
    }
  };

  // Helper: get next slot sequentially from lastSlot
  const getNextSlotSequential = (lastSlot, slots) => {
    const base = new Date(lastSlot.getTime() + 2 * 60 * 60 * 1000);
    for (let weeksAhead = 0; weeksAhead < 8; weeksAhead++) {
      for (const slot of slots) {
        const dayDiff = (slot.day - base.getUTCDay() + 7) % 7;
        const candidate = new Date(base);
        candidate.setUTCDate(base.getUTCDate() + dayDiff + weeksAhead * 7);
        candidate.setUTCHours(slot.hour, 0, 0, 0);
        candidate.setUTCMinutes(0, 0, 0);
        if (candidate > base) return candidate;
      }
    }
    return new Date(base.getTime() + 48 * 60 * 60 * 1000);
  };

  // ── Approve & Publish — routes all platforms to buffer_queue ────────────────
  const handleApprove = async () => {
    if (!current) return;
    const selected = Object.keys(platforms).filter(p => platforms[p]);
    if (!selected.length) { showToast('Select at least one platform', 'error'); return; }

    const caption = editedContent || current.sourceText || current.title || '';
    if (!caption.trim()) { showToast('Add a caption before publishing', 'error'); return; }

    setPublishing(true);
    const mediaUrl = current.pinterestUrl || current.imageUrl || current.videoUrl || current.outputUrl || null;
    const errors   = [];
    const succeeded = [];

    for (const platform of selected) {
      // YouTube Shorts used to go through the same Buffer-CSV path as every
      // other platform (a manual, temporary workaround). Approving it now
      // marks the video "approved for YouTube" in the asset store instead —
      // it shows up in the YouTube tab for a final review/date pick and a
      // real publish, rather than producing a CSV to upload by hand.
      if (platform === 'youtube shorts' && mediaUrl && isVideo(mediaUrl)) {
        try {
          const res = await authFetch(`${API_URL}/api/assets`, {
            method: 'POST',
            body: JSON.stringify({
              id: 'vid-' + current.id,
              type: 'video',
              url: mediaUrl,
              title: current.title || '',
              platform: 'youtube shorts',
              description: caption,
              idea_id: current.ideaId || null,
              approved_for_youtube: true,
            }),
          });
          const data = await res.json();
          if (!data.ok) throw new Error(data.error || 'Approve for YouTube failed');
          succeeded.push(platform);
        } catch (e) {
          errors.push(`${platform}: ${e.message}`);
        }
        continue;
      }

      try {
        const res = await authFetch(`${API_URL}/api/buffer-queue`, {
          method: 'POST',
          body: JSON.stringify({
            platform,
            title:         current.title || '',
            content:       caption,
            image_url:     mediaUrl && !isVideo(mediaUrl) ? mediaUrl : null,
            video_url:     mediaUrl && isVideo(mediaUrl)  ? mediaUrl : null,
            pinterest_url: current.pinterestUrl || null,
            idea_id:       current.ideaId || null,
            queue_item_id: current.id,
          }),
        });
        const data = await res.json();
        if (!data.success) throw new Error(data.error || 'Queue failed');
        succeeded.push(platform);
      } catch (e) {
        errors.push(`${platform}: ${e.message}`);
      }
    }

    // Mark queue item published if at least one platform succeeded
    if (succeeded.length > 0) {
      await authFetch(`${API_URL}/api/queue/${current.id}`, {
        method: 'PATCH',
        body: JSON.stringify({ status: 'published' }),
      }).catch(e => console.warn('[queue patch]', e.message));
      fetchBufferCounts();

      // Sprint 6.2: archive the idea now that its full lifecycle is complete.
      // Only runs on success (we're already inside succeeded.length > 0), and never
      // blocks the publish flow the user already completed if archiving itself fails.
      if (current.ideaId) {
        try {
          const publishedAt = new Date().toISOString();

          // 1. Mark the idea itself published (previously never touched by this function)
          const ideaRes = await authFetch(`${API_URL}/api/content/${current.ideaId}`, {
            method: 'PATCH',
            body: JSON.stringify({ status: 'published', publishedAt }),
          });
          const ideaData = await ideaRes.json();

          if (ideaData.success) {
            // 2. Pull all generated platform cards for this idea
            const cardsRes = await authFetch(`${API_URL}/api/content/cards?ideaId=${current.ideaId}`);
            const cardsJson = await cardsRes.json();

            // 3. Snapshot whatever media URLs are already on the approved queue item
            const mediaUrls = [current.imageUrl, current.videoUrl, current.pinterestUrl, current.outputUrl].filter(Boolean);

            // 4. Write the permanent archive record (independent of generated_content's
            //    90-day cleanup route, so it survives long after the source idea would be purged)
            await authFetch(`${API_URL}/api/archive`, {
              method: 'POST',
              body: JSON.stringify({
                ideaId: current.ideaId,
                ideaData: ideaData.item,
                cardsData: cardsJson.cards || [],
                mediaUrls,
              }),
            });
          } else {
            console.warn('[archive] idea PATCH failed, skipping archive:', ideaData.error);
          }
        } catch (e) {
          console.warn('[archive] archive step failed', e.message);
        }
      }

      // Track in scheduled_posts so Calendar tab shows pending items.
      // Slot times below are research-based best-posting-times per platform,
      // converted from Central Time (UTC-5 summer/UTC-6 winter — using
      // summer offset since that's the common case) to UTC. day: 0=Sun..6=Sat.
      // Any platform NOT listed here falls back to a single Monday 2pm UTC
      // slot (see getNextApproveSlot below) — instagram/twitter/threads used
      // to hit that fallback and all pile onto the same day; now covered.
      // Every platform gets its own day(s) so posts spread evenly instead of
      // stacking onto two days while the rest of the week sits empty (see the
      // real Sep 2026 calendar that surfaced this). Each day carries two
      // platforms — one at 9am CT, one at 5pm CT — so a shared day never has
      // a time collision and no platform repeats a day. Kept in sync with
      // the matching PLATFORM_SLOTS table in weeklyScheduler.js; facebook/
      // tiktok/linkedin (Buffer) now schedule from here too, not Buffer's
      // own auto-timing. (twitter keeps Mon/Wed — not reliably publishing
      // yet; revisit when it is.)
      const platSlots = {
        bluesky:   [{ day: 1, hour: 14 }, { day: 5, hour: 14 }],  // Mon/Fri 9am CT
        mastodon:  [{ day: 2, hour: 14 }, { day: 6, hour: 14 }],  // Tue/Sat 9am CT
        instagram: [{ day: 3, hour: 14 }, { day: 0, hour: 14 }],  // Wed/Sun 9am CT
        threads:   [{ day: 4, hour: 14 }, { day: 0, hour: 22 }],  // Thu 9am CT, Sun 5pm CT
        facebook:  [{ day: 1, hour: 22 }, { day: 4, hour: 22 }],  // Mon/Thu 5pm CT
        tiktok:    [{ day: 2, hour: 22 }, { day: 5, hour: 22 }],  // Tue/Fri 5pm CT
        linkedin:  [{ day: 3, hour: 22 }, { day: 6, hour: 22 }],  // Wed/Sat 5pm CT
        twitter:   [{ day: 1, hour: 14 }, { day: 3, hour: 14 }],  // Mon/Wed 9am CT
        pinterest: [{ day: 2, hour: 12 }, { day: 5, hour: 12 }],
      };
      // Kept in sync with nextSlot() in weeklyScheduler.js — see the two
      // subtleties documented there: (1) diff is a bare `% 7` with no `|| 7`,
      // or a platform's last slot's own weekday would make it re-pick the
      // same slot a week out instead of alternating; (2) within a week, take
      // the SOONEST valid slot, not the first in array order, or a 5pm slot
      // (whose +2h buffer rolls past midnight) sends the next pick to the
      // platform's first-listed day even when its other day is sooner.
      const getNextApproveSlot = (startFrom, platform) => {
        const slots = platSlots[platform] || [{ day: 1, hour: 14 }];
        const base = new Date(startFrom.getTime() + 2 * 60 * 60 * 1000);
        for (let w = 0; w < 8; w++) {
          let best = null;
          for (const slot of slots) {
            const c = new Date(base);
            c.setUTCDate(c.getUTCDate() + w * 7);
            const diff = (slot.day - c.getUTCDay() + 7) % 7;
            c.setUTCDate(c.getUTCDate() + diff);
            c.setUTCHours(slot.hour, 0, 0, 0);
            if (c > base && (!best || c < best)) best = c;
          }
          if (best) return best.toISOString();
        }
        return new Date(startFrom.getTime() + 48 * 60 * 60 * 1000).toISOString();
      };
      for (const platform of succeeded) {
        try {
          let lastSlotMs = null;
          try {
            const lsRes = await authFetch(`${API_URL}/api/scheduled-calendar/last-slot?platform=${platform}`);
            const lsData = await lsRes.json();
            lastSlotMs = lsData.lastSlot || null;
          } catch (e) {
            console.warn('[last-slot] fetch failed, falling back to now', e.message);
          }
          const startFrom = lastSlotMs ? new Date(lastSlotMs) : new Date();
          await authFetch(`${API_URL}/api/scheduled-calendar`, {
            method: 'POST',
            body: JSON.stringify({
              platform,
              content: caption,
              mediaUrl: mediaUrl || null,
              scheduledAt: getNextApproveSlot(startFrom, platform),
              ideaId: current.ideaId || null,
              title: current.title || '',
            }),
          });
        } catch (e) {
          console.warn('[calendar track]', platform, e.message);
        }
      }
    }

    setPublishing(false);

    if (errors.length === 0) {
      showToast(`Queued for ${succeeded.join(' & ')} ✓`);
      setItems(prev => { const next = prev.filter((_, i) => i !== activeIdx); if (onCountChange) onCountChange(next.length); return next; });
      setActiveIdx(0);
    } else if (succeeded.length > 0) {
      showToast(`Partial: ${errors.join(' | ')}`, 'error');
      setItems(prev => { const next = prev.filter((_, i) => i !== activeIdx); if (onCountChange) onCountChange(next.length); return next; });
      setActiveIdx(0);
    } else {
      showToast(errors.join(' | '), 'error');
    }
  };

  // ── Generate Caption ─────────────────────────────────────────────────────────
  const handleGenerateCaption = async () => {
    if (!current || generatingCaption) return;
    setGeneratingCaption(true);
    const topic = current.sourceText || current.title || current.caption || '';
    const platform = Object.keys(platforms).find(p => platforms[p]) || 'bluesky';
    try {
      const res = await fetch('https://sh-claude-proxy.strategichonesty.workers.dev', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          model: 'claude-haiku-4-5-20251001',
          max_tokens: 300,
          system: 'You write social media captions for Gopu Shrestha, a Nepali-American Senior Program Manager, author, and leadership coach. Brand voice: warm, mentor-toned, conversational. Mix short punchy sentences with longer reflective ones. Anchor in Nepal/immigration journey when relevant. Core phrase: "Be Good. Do Good. Do Well." Audience: parents, early-career pros, Agile practitioners. No jargon, no hype. Reading level: 8th-10th grade. Always end with strategichonesty.com. Keep under 270 characters for Bluesky.',
          messages: [{ role: 'user', content: `Write a punchy social media caption about: "${topic.slice(0, 300)}". Rules: max 2 sentences, under 220 characters total, no hashtags, end with strategichonesty.com. Hook them in line 1. Hit the heart in line 2. Return only the caption text, nothing else.` }],
        }),
      });
      const data = await res.json();
      const caption = (data?.content?.[0]?.text || '').trim();
      if (caption) setEditedContent(caption);
    } catch (e) {
      console.warn('[ReviewPublish] caption gen failed:', e.message);
    }
    setGeneratingCaption(false);
  };

  // ── Generate All Captions ───────────────────────────────────────────────────
  const isRawPrompt = (text) => {
    if (!text) return true;
    const raw = ['nepali-american male', 'nepali-american', 'a confident professional', 'photorealistic', 'cinematic scene'];
    const lower = text.toLowerCase();
    return text.length > 200 || raw.some(r => lower.includes(r));
  };

  const handleGenerateAllCaptions = async () => {
    if (generatingAll) return;
    const needsCaption = items.filter(item => isRawPrompt(item.sourceText));
    if (needsCaption.length === 0) {
      showToast('All items already have captions ✓');
      return;
    }
    setGeneratingAll(true);
    let done = 0;
    for (const item of needsCaption) {
      setGenerateAllProgress(`Generating ${done + 1}/${needsCaption.length}...`);
      const topic = item.sourceText || item.title || '';
      try {
        const res = await fetch('https://sh-claude-proxy.strategichonesty.workers.dev', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            model: 'claude-haiku-4-5-20251001',
            max_tokens: 300,
            system: 'You write social media captions for Gopu Shrestha, a Nepali-American Senior Program Manager, author, and leadership coach. Brand voice: warm, mentor-toned, conversational. Mix short punchy sentences with longer reflective ones. Anchor in Nepal/immigration journey when relevant. Core phrase: "Be Good. Do Good. Do Well." Audience: parents, early-career pros, Agile practitioners. No jargon, no hype. Reading level: 8th-10th grade. Always end with strategichonesty.com. Keep under 270 characters for Bluesky.',
            messages: [{ role: 'user', content: `Write a punchy social media caption about: "${topic.slice(0, 300)}". Rules: max 2 sentences, under 220 characters total, no hashtags, end with strategichonesty.com. Hook them in line 1. Hit the heart in line 2. Return only the caption text, nothing else.` }],
          }),
        });
        const data = await res.json();
        const caption = (data?.content?.[0]?.text || '').trim();
        if (caption) {
          // Save caption back to queue item via PATCH
          await authFetch(`${API_URL}/api/queue/${item.id}`, {
            method: 'PATCH',
            body: JSON.stringify({ sourceText: caption }),
          }).catch(e => console.warn('[generateAll] patch failed:', e.message));
          // Update local state too
          setItems(prev => prev.map(i => i.id === item.id ? { ...i, sourceText: caption } : i));
        }
      } catch (e) {
        console.warn('[generateAll] failed for', item.id, e.message);
      }
      done++;
      // Small delay to avoid rate limiting
      await new Promise(r => setTimeout(r, 800));
    }
    setGeneratingAll(false);
    setGenerateAllProgress('');
    showToast(`✅ Captions generated for ${done} items`);
  };

  // ── Schedule All ─────────────────────────────────────────────────────────────
  const handleScheduleAll = async () => {
    if (!scheduleStartDate) return;
    const unitMs = { days: 86400000, hours: 3600000, minutes: 60000 };
    const intervalMs = scheduleInterval * unitMs[scheduleUnit];
    const baseTime = new Date(scheduleStartDate).getTime();
    setScheduling(true);
    setScheduleLog([]);
    const log = [];

    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      const scheduledDate = new Date(baseTime + i * intervalMs).toISOString();
      const content = item.sourceText || item.title || 'Be Good. Do Good. Do Well. — Gopu Shrestha | strategichonesty.com';
      const mediaUrl = item.pinterestUrl || item.imageUrl || item.videoUrl || item.outputUrl || null;

      const platforms_to_schedule = [
        { integrationId: 'cmq76myko0001pj8stvtllcuy', platformType: 'bluesky',  truncate: 280 },
        { integrationId: 'cmqbijehr0001o797udmsniel', platformType: 'mastodon', truncate: null },
      ];

      for (const plat of platforms_to_schedule) {
        const text = plat.truncate && content.length > plat.truncate
          ? content.slice(0, plat.truncate - 3) + '...'
          : content;
        const images = mediaUrl && !mediaUrl.startsWith('data:') ? [{ id: 'sh-media-1', path: mediaUrl }] : [];
        try {
          const res = await fetch(`${BACKEND}/scheduler/posts/postiz/publish`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'x-api-key': API_KEY },
            body: JSON.stringify({
              integrationId: plat.integrationId,
              content: text,
              platformType: plat.platformType,
              images,
              type: 'schedule',
              date: scheduledDate,
            }),
          });
          const data = await res.json();
          log.push({
            title: item.title || `Item ${i + 1}`,
            platform: plat.platformType,
            date: scheduledDate,
            ok: res.ok && data.success,
            message: res.ok && data.success ? '✅ Scheduled' : `❌ ${JSON.stringify(data.error || data)}`,
          });
        } catch (err) {
          log.push({ title: item.title || `Item ${i + 1}`, platform: plat.platformType, date: scheduledDate, ok: false, message: '❌ Network error' });
        }
        // Small delay to avoid Postiz rate limiting
        await new Promise(r => setTimeout(r, 600));
      }
    }

    setScheduleLog(log);
    setScheduling(false);
  };

  // ── Skip ─────────────────────────────────────────────────────────────────────
  const handleSkip = async () => {
    if (!current) return;
    if (!window.confirm('Remove this item from your queue? This cannot be undone.')) return;
    try {
      await authFetch(`${API_URL}/api/queue/${current.id}`, {
        method: 'PATCH',
        body: JSON.stringify({ status: 'skipped' }),
      });
      showToast('Skipped');
      
      setItems(prev => { const next = prev.filter((_, i) => i !== activeIdx); if (onCountChange) onCountChange(next.length); return next; });
      setActiveIdx(0);
    } catch (err) {
      showToast(err.message, 'error');
    }
  };

  // ── Loading / empty states ───────────────────────────────────────────────────
  if (loading) {
    return (
      <div style={S.center}>
        <div style={{ fontSize: 32, animation: 'spin 1s linear infinite' }}>⟳</div>
        <p style={S.emptySub}>Loading completed assets…</p>
        <style>{`@keyframes spin{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}`}</style>
      </div>
    );
  }

  if (!items.length) {
    return (
      <div>
        <YoutubeQueue />
        <div style={S.center}>
          <div style={{ fontSize: 44 }}>🎨</div>
          <p style={S.emptyHead}>No completed assets yet</p>
          <p style={S.emptySub}>Finished images and videos from Media Studio appear here for you to approve. If this stays empty, nothing has been sent to Media Studio yet: on the Repurpose page, open "More options" and click Approve &amp; Auto-Produce.</p>
          <button onClick={fetchCompleted} style={S.refreshBtn}>↻ Check now</button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ fontFamily: 'inherit' }}>
      {toast && (
        <div style={{ ...S.toast, background: toast.type === 'error' ? '#dc2626' : '#16a34a' }}>
          {toast.msg}
        </div>
      )}

      <div style={{ fontSize: 12, color: '#64748b', marginBottom: 10 }}>Finished images and videos from Media Studio. Approve each one to move it to Waiting to Publish.</div>
      <YoutubeQueue />

      {/* Card */}
      {current && (
        <div style={S.card}>
          <div style={S.metaRow}>
          <span style={S.badge}>{current.contentType || 'asset'}</span>
            <span style={{ fontSize: 11, color: '#64748b' }}>ID {current.id}</span>
            {current.platform && (
              <span style={{ ...S.badge, background: '#f1f5f9', color: '#1B2B4B' }}>{current.platform}</span>
            )}
            {current.createdAt && (
              <span style={{ fontSize: 11, color: '#94a3b8' }} title={new Date(current.createdAt).toISOString()}>
                🕐 Generated {new Date(current.createdAt).toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
              </span>
            )}
          </div>

          {/* Quick actions, duplicated at the top — this screen has no
              independent scroll container (the whole page scrolls, see
              SocialHub.jsx's .sh-root), and the action row sits at the very
              end of the card with nothing after it, so a sticky bottom
              position had nothing to "stick" against and never actually
              helped. This copy makes Approve/Delete reachable the instant
              the card opens, no scrolling past the preview/caption needed.
              The original pair further down still works too, for after
              you've actually read through the content. */}
          <div style={{ ...S.actions, position: 'static', boxShadow: 'none', marginBottom: 14, padding: 0 }}>
            <button onClick={handleApprove} disabled={publishing} style={{
              ...S.btn,
              background: publishing ? '#94a3b8' : (editedContent || current?.sourceText || current?.title || '').trim() ? '#C8963E' : '#ef4444',
              cursor: publishing ? 'not-allowed' : 'pointer',
            }} title={(editedContent || current?.sourceText || current?.title || '').trim() ? 'Queue this item for selected platforms' : 'Add a caption before publishing'}>
              {publishing ? 'Queuing…' : (editedContent || current?.sourceText || current?.title || '').trim() ? '✅ Approve & Publish' : '⚠️ Add Caption First'}
            </button>
            <button onClick={handleSkip} disabled={publishing} style={{
              ...S.btn, background: '#dc2626', flex: '0 0 auto', padding: '12px 20px',
            }} title="Remove this item — deletes it from your queue permanently">
              🗑 Delete
            </button>
          </div>

          {/* Preview — platform mockup (Sprint 14) */}
          {(() => {
            const previewUrl = current.outputUrl || current.imageUrl || current.videoUrl || current.pinterestUrl || null;
            return <PlatformMockup
              platform={current.platform}
              caption={editedContent || current.sourceText || current.title || ''}
              mediaUrl={previewUrl}
              isVideoUrl={previewUrl ? isVideo(previewUrl) : false}
            />;
          })()}

          {/* Asset URLs */}
          <AssetUrlEditor item={current} onSaved={(id, field, val) => {
            setItems(prev => prev.map(i => i.id === id ? { ...i, [field]: val } : i));
          }} />

          {/* Caption */}
          <div style={{ padding: '0 4px 8px' }}>
            <textarea
              value={editedContent}
              onChange={e => setEditedContent(e.target.value)}
              rows={5}
              style={{ width: '100%', fontSize: 13, lineHeight: 1.5, padding: '8px 10px', borderRadius: 8, border: '1px solid #e2e8f0', resize: 'vertical', boxSizing: 'border-box', fontFamily: 'inherit', color: '#1e293b' }}
            />
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 3 }}>
              <button
                onClick={handleGenerateCaption}
                disabled={generatingCaption}
                style={{
                  padding: '4px 12px', borderRadius: 6, border: 'none',
                  background: generatingCaption ? '#e5e7eb' : 'linear-gradient(135deg, #1B2B4B, #2d4a7a)',
                  color: generatingCaption ? '#9ca3af' : '#C8963E',
                  fontSize: 11, fontWeight: 700, cursor: generatingCaption ? 'wait' : 'pointer',
                  fontFamily: 'inherit',
                }}
              title="Use AI to write a 2-sentence caption for this item">
                {generatingCaption ? '⏳ Writing...' : '✨ Generate Caption'}
              </button>
              <span style={{ fontSize: 11, color: editedContent.length > 300 ? '#dc2626' : '#94a3b8' }}>
                {editedContent.length}/300 Bluesky limit
              </span>
            </div>
          </div>

          {/* Platform row, actions, skip — unchanged below */}

          {/* Platform select — all 6 */}
          <div style={{ marginBottom: 14 }}>
            <span style={{ fontSize: 12, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.8, display: 'block', marginBottom: 8 }}>Publish to:</span>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {[
                { key: 'bluesky',   label: '🦋 Bluesky',   color: '#0085ff' },
                { key: 'mastodon',  label: '🐘 Mastodon',  color: '#6364ff' },
                { key: 'twitter',   label: '🐦 X / Twitter', color: '#000000' },
                { key: 'threads',   label: '🧵 Threads',   color: '#000000' },
                { key: 'facebook',  label: '📥 Facebook',  color: '#1877F2' },
                { key: 'instagram', label: '📸 Instagram', color: '#E1306C' },
                { key: 'linkedin',  label: '💼 LinkedIn',  color: '#0A66C2' },
                { key: 'tiktok',         label: '📥 TikTok',          color: '#010101' },
                { key: 'facebook reels', label: '📘 Facebook Reels', color: '#1877F2' },
                { key: 'instagram reels',label: '🎬 Instagram Reels',color: '#E1306C' },
                { key: 'pinterest',      label: '📌 Pinterest',       color: '#E60023' },
                { key: 'youtube shorts', label: '▶️ YouTube Shorts',  color: '#FF0000' },
              ].map(({ key, label, color }) => (
                <button key={key} onClick={() => setPlatforms(prev => ({ ...prev, [key]: !prev[key] }))} style={{
                  display: 'flex', alignItems: 'center', gap: 6,
                  padding: '6px 14px', borderRadius: 20, border: `2px solid ${platforms[key] ? color : '#e2e8f0'}`,
                  background: platforms[key] ? color : '#f8fafc',
                  color: platforms[key] ? '#fff' : '#94a3b8',
                  fontSize: 12, fontWeight: 600, cursor: 'pointer', transition: 'all 0.15s',
                }}>
                  <span style={{
                    width: 14, height: 14, borderRadius: '50%',
                    border: `2px solid ${platforms[key] ? '#fff' : '#cbd5e1'}`,
                    background: platforms[key] ? '#fff' : 'transparent',
                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                  }}>
                    {platforms[key] && <span style={{ width: 6, height: 6, borderRadius: '50%', background: color, display: 'block' }} />}
                  </span>
                  {label}
                </button>
              ))}
            </div>
          </div>

          {/* Actions */}
          <div style={S.actions}>
            <button onClick={handleApprove} disabled={publishing} style={{
              ...S.btn,
              background: publishing ? '#94a3b8' : (editedContent || current?.sourceText || current?.title || '').trim() ? '#C8963E' : '#ef4444',
              cursor: publishing ? 'not-allowed' : 'pointer',
            }} title={(editedContent || current?.sourceText || current?.title || '').trim() ? 'Queue this item for selected platforms' : 'Add a caption before publishing'}>
              {publishing ? 'Queuing…' : (editedContent || current?.sourceText || current?.title || '').trim() ? '✅ Approve & Publish' : '⚠️ Add Caption First'}
            </button>
            <button onClick={handleSkip} disabled={publishing} style={{
              ...S.btn, background: '#dc2626', flex: '0 0 auto', padding: '12px 20px',
            }} title="Remove this item — deletes it from your queue permanently">
              🗑 Delete
            </button>
          </div>
        </div>
      )}

      {/* Queue row list */}
      {items.length > 0 && (
        <div style={{ marginTop: 24 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
            <span style={{ fontSize: 12, color: '#94a3b8', fontWeight: 500 }}>{items.length} item{items.length !== 1 ? 's' : ''} ready</span>
            <div style={{ display: 'flex', gap: 8 }}>

              <button onClick={fetchCompleted} style={{ ...S.stripBtn, background: '#f1f5f9', color: '#64748b', padding: '4px 10px' }} title="Reload completed assets from Media Studio">↻ Refresh</button>

              <button onClick={fetchBufferCounts} style={{ ...S.stripBtn, background: '#f1f5f9', color: '#64748b', padding: '4px 10px' }} title="Refresh queue counts">↻ Counts</button>
            </div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {(() => {
              const grouped = {};
              items.forEach((item, i) => {
                const plat = (item.platform || 'other').toLowerCase();
                if (!grouped[plat]) grouped[plat] = [];
                grouped[plat].push({ item, i });
              });
              return Object.entries(grouped).map(([plat, entries]) => {
                const isOpen = openPlatItems[plat] !== false;
                return (
                  <div key={plat} style={{ borderRadius: 10, overflow: 'hidden', border: '1px solid #e2e8f0' }}>
                    <div onClick={() => setOpenPlatItems(p => ({ ...p, [plat]: !isOpen }))}
                      style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '9px 14px', background: '#1a2035', cursor: 'pointer', userSelect: 'none' }}>
                      <span style={{ fontSize: 13, fontWeight: 700, color: '#C8963E', textTransform: 'uppercase' }}>{plat}</span>
                      <span style={{ fontSize: 11, color: '#8b90a0' }}>({entries.length})</span>
                      <span style={{ fontSize: 11, color: '#8b90a0', marginLeft: 'auto' }}>{isOpen ? '▲ Hide' : '▼ Show'}</span>
                    </div>
                    {isOpen && entries.map(({ item, i }) => (
                      <div key={item.id} onClick={() => setActiveIdx(i)} style={{
                        display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px',
                        borderBottom: '1px solid #f0f4f8',
                        background: '#fff', cursor: 'pointer', opacity: i === activeIdx ? 1 : 0.85,
                        borderLeft: i === activeIdx ? '3px solid #C8963E' : '3px solid transparent',
                      }}>
                        <div style={{ width: 48, height: 48, borderRadius: 8, background: '#f0f4f8', flexShrink: 0, overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          {isVideo(item.outputUrl) || ['reel','short_video','video','yt_short'].includes(item.contentType)
                            ? <video src={item.outputUrl} muted style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                            : isBase64Image(item.outputUrl) || isHttpImage(item.outputUrl)
                              ? <img src={item.outputUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                              : <span style={{ fontSize: 20 }}>🖼️</span>
                          }
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 13, fontWeight: 500, color: '#1e293b', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {item.title || item.sourceText?.slice(0, 60) || 'Untitled'}
                          </div>
                          <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>{item.contentType || 'asset'}</div>
                        </div>
                        <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexShrink: 0 }}>
                          {i === activeIdx && <span style={{ fontSize: 11, color: '#C8963E', fontWeight: 500 }}>Viewing</span>}
                        </div>
                      </div>
                    ))}
                  </div>
                );
              });
            })()}
          </div>
        </div>
      )}
      {/* Buffer Queue Counter Cards */}
      <div style={{ marginTop: 20, marginBottom: 8 }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 10 }}>Queue Status</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {[
            { key: 'bluesky',   label: '🦋 Bluesky',   color: '#0085ff', cap: null, note: 'Tue 9am · Thu 12pm CT' },
            { key: 'mastodon',  label: '🐘 Mastodon',  color: '#6364ff', cap: null, note: 'Wed 10am · Fri 2pm CT' },
            { key: 'twitter',   label: '🐦 X / Twitter', color: '#000000', cap: 10,   note: 'Buffer CSV' },
            { key: 'threads',   label: '🧵 Threads',   color: '#000000', cap: 10,   note: 'Buffer CSV' },
            { key: 'facebook',  label: '📥 Facebook',  color: '#1877F2', cap: 10,   note: 'Buffer CSV' },
            { key: 'instagram', label: '📸 Instagram', color: '#E1306C', cap: 10,   note: 'Buffer CSV' },
            { key: 'linkedin',  label: '📥 LinkedIn',  color: '#0A66C2', cap: 10,   note: 'Buffer CSV' },
            { key: 'tiktok',         label: '📥 TikTok',          color: '#010101', cap: 10, note: 'Buffer CSV' },
            { key: 'facebook reels', label: '📘 Facebook Reels', color: '#1877F2', cap: 10, note: 'Buffer CSV' },
            { key: 'instagram reels',label: '🎬 Instagram Reels',color: '#E1306C', cap: 10, note: 'Buffer CSV' },
            { key: 'pinterest',      label: '📌 Pinterest',       color: '#E60023', cap: 10, note: 'Pinterest CSV' },
            { key: 'youtube shorts', label: '▶️ YouTube Shorts',  color: '#FF0000', cap: 10, note: 'Buffer CSV' },
          ].map(({ key, label, color, cap, note }) => {
            const count = bufferCounts[key] || 0;
            const pct   = cap ? Math.min(count / cap, 1) : 0;
            const ready = cap ? count >= cap : count > 0;
            const isExpanded = expandedPlatform === key;
            const platformItems = bufferPosts.filter(p => p.platform === key);
            return (
              <div key={key} style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10, padding: '10px 14px' }}>
                <div onClick={() => setExpandedPlatform(isExpanded ? null : key)} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: cap ? 6 : 0, cursor: 'pointer', background: '#7c3aed', margin: '-10px -14px 6px -14px', padding: '10px 14px', borderRadius: '10px 10px 0 0' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontSize: 10, color: '#fff', transform: isExpanded ? 'rotate(90deg)' : 'rotate(0)', transition: 'transform .15s', display: 'inline-block' }}>▶</span>
                    <span style={{ fontWeight: 700, fontSize: 13, color: '#fff' }}>{label}</span>
                    <span style={{ fontSize: 11, color: '#e9d8fd' }}>{note}</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                    <span style={{ fontWeight: 700, fontSize: 13, color: '#fff' }}>
                      {cap ? `${count}/10` : count}
                    </span>
                    {cap && (
                      <button
                        onClick={(e) => { e.stopPropagation(); handleExportCsv(key, label); }}
                        style={{ ...S.stripBtn, background: color, color: '#fff', padding: '3px 10px', fontSize: 11 }}
                      >⬇ Download CSV{count > 0 ? ` (${count})` : ''}</button>
                    )}
                    {!cap && ready && (
                      <button
                        onClick={(e) => { e.stopPropagation(); handlePublishToPostiz(key); }}
                        style={{ ...S.stripBtn, background: color, color: '#fff', padding: '3px 10px', fontSize: 11 }}
                      >🚀 Publish to Postiz</button>
                    )}
                  </div>
                </div>
                {cap && (
                  <div style={{ height: 6, background: '#f1f5f9', borderRadius: 3, overflow: 'hidden' }}>
                    <div style={{ height: '100%', width: `${pct * 100}%`, background: ready ? color : '#94a3b8', borderRadius: 3, transition: 'width 0.3s' }} />
                  </div>
                )}
                {isExpanded && (
                  <div style={{ marginTop: 10, paddingTop: 10, borderTop: '1px solid #f1f5f9', display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {!cap && platformItems.length > 0 && (
                      <span style={{ fontSize: 10, color: '#94a3b8', background: '#f1f5f9', borderRadius: 6, padding: '2px 8px', alignSelf: 'flex-start', marginBottom: 2 }}>scheduled slots — not a 10-cap</span>
                    )}
                    {platformItems.length === 0 && (
                      <div style={{ fontSize: 11, color: '#94a3b8', fontStyle: 'italic', padding: '4px 0' }}>No items queued for this platform.</div>
                    )}
                    {platformItems.map(post => (
                      <div key={post.id} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, padding: '6px 8px', background: '#f8fafc', borderRadius: 7 }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 12, fontWeight: 600, color: '#1B2B4B', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{post.title || 'Untitled'}</div>
                          <div style={{ fontSize: 11, color: '#94a3b8', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{(post.content || '').slice(0, 80)}</div>
                        </div>
                        {(post.video_url || post.image_url) && (
                          <a
                            href={post.video_url || post.image_url}
                            download
                            onClick={e => e.stopPropagation()}
                            style={{ padding: '8px 12px', borderRadius: 6, fontSize: 12, fontWeight: 600, background: '#1B2B4B', color: '#C8963E', border: '1px solid #C8963E', textDecoration: 'none', whiteSpace: 'nowrap', flexShrink: 0, minHeight: 36, display: 'flex', alignItems: 'center' }}
                          >⬇</a>
                        )}
                        <button
                          onClick={(e) => { e.stopPropagation(); handleRemoveQueueItem(post.id); }}
                          disabled={removingId === post.id}
                          style={{ background: 'none', border: 'none', color: '#94a3b8', cursor: removingId === post.id ? 'default' : 'pointer', fontSize: 15, padding: '6px 8px', minWidth: 36, minHeight: 36, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, borderRadius: 6 }}
                          title="Remove from queue"
                        >{removingId === post.id ? '…' : '✕'}</button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {showScheduleModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: '#fff', borderRadius: 14, padding: 28, minWidth: 'min(380px, 92vw)', maxWidth: 460, width: '92%', boxShadow: '0 8px 40px rgba(0,0,0,.18)' }}>
            <h2 style={{ margin: '0 0 20px', fontSize: 17, color: '#1B2B4B' }}>📅 Schedule All Posts</h2>

            <label style={{ display: 'block', marginBottom: 5, fontSize: 12, color: '#64748b', fontWeight: 600 }}>START DATE & TIME</label>
            <input type="datetime-local" value={scheduleStartDate} onChange={e => setScheduleStartDate(e.target.value)} min={new Date(Date.now() + 5 * 60000).toISOString().slice(0, 16)}
              style={{ width: '100%', padding: '8px 10px', borderRadius: 7, border: '1px solid #e2e8f0', fontSize: 13, marginBottom: 16, boxSizing: 'border-box', color: '#1e293b' }} />

            <label style={{ display: 'block', marginBottom: 5, fontSize: 12, color: '#64748b', fontWeight: 600 }}>INTERVAL BETWEEN POSTS</label>
            <div style={{ display: 'flex', gap: 8, marginBottom: 22 }}>
              <input type="number" min="1" value={scheduleInterval} onChange={e => setScheduleInterval(Number(e.target.value))}
                style={{ width: 72, padding: '8px 10px', borderRadius: 7, border: '1px solid #e2e8f0', fontSize: 13, color: '#1e293b' }} />
              <select value={scheduleUnit} onChange={e => setScheduleUnit(e.target.value)}
                style={{ flex: 1, padding: '8px 10px', borderRadius: 7, border: '1px solid #e2e8f0', fontSize: 13, color: '#1e293b' }}>
                <option value="minutes">Minutes</option>
                <option value="hours">Hours</option>
                <option value="days">Days</option>
              </select>
            </div>

            {scheduleLog.length > 0 && (
              <div style={{ maxHeight: 160, overflowY: 'auto', marginBottom: 18, background: '#f8fafc', borderRadius: 7, padding: '10px 12px', fontSize: 12, lineHeight: 1.9, border: '1px solid #e2e8f0' }}>
                {scheduleLog.map((entry, i) => (
                  <div key={i}>
                    <span style={{ color: entry.ok ? '#16a34a' : '#dc2626' }}>{entry.message}</span>
                    {' — '}<span style={{ color: '#1B2B4B', fontWeight: 600 }}>{entry.title}</span>
                    {' '}<span style={{ color: '#94a3b8' }}>({entry.platform})</span>
                    {' @ '}<span style={{ color: '#64748b' }}>{new Date(entry.date).toLocaleString()}</span>
                  </div>
                ))}
              </div>
            )}

            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button onClick={() => { setShowScheduleModal(false); setScheduleLog([]); }} disabled={scheduling}
                style={{ padding: '9px 18px', borderRadius: 7, border: '1px solid #e2e8f0', background: '#fff', color: '#64748b', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
                {scheduleLog.length > 0 ? 'Close' : 'Cancel'}
              </button>
              <button onClick={handleScheduleAll} disabled={scheduling || !scheduleStartDate}
                style={{ padding: '9px 22px', borderRadius: 7, border: 'none', background: scheduling || !scheduleStartDate ? '#94a3b8' : '#C8963E', color: '#fff', cursor: scheduling || !scheduleStartDate ? 'not-allowed' : 'pointer', fontSize: 13, fontWeight: 700 }}>
                {scheduling ? 'Scheduling…' : `Schedule ${items.length} Posts`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// PlatformMockup now lives in its own file so other views (e.g. Awaiting
// Publish) can reuse it without duplicating the whole preview.

const S = {
  center:      { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: 340, gap: 12, textAlign: 'center' },
  emptyHead:   { fontSize: 16, fontWeight: 700, color: '#1B2B4B', margin: 0 },
  emptySub:    { fontSize: 13, color: '#94a3b8', margin: 0 },
  refreshBtn:  { marginTop: 8, padding: '8px 20px', borderRadius: 8, border: '1px solid #e2e8f0', background: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 600, color: '#1B2B4B' },
  toast:       { position: 'fixed', top: 18, right: 18, padding: '11px 20px', borderRadius: 9, color: '#fff', fontWeight: 600, fontSize: 13, zIndex: 9999, boxShadow: '0 4px 14px rgba(0,0,0,.15)' },
  strip:       { display: 'flex', gap: 7, marginBottom: 18, flexWrap: 'wrap' },
  stripBtn:    { padding: '5px 13px', borderRadius: 20, border: 'none', cursor: 'pointer', fontSize: 12, fontWeight: 600 },
  card:        { background: '#fff', borderRadius: 12, boxShadow: '0 2px 12px rgba(0,0,0,.08)', padding: 22, border: '1px solid #e2e8f0' },
  metaRow:     { display: 'flex', alignItems: 'center', gap: 9, marginBottom: 14, flexWrap: 'wrap' },
  badge:       { background: '#1B2B4B', color: '#C8963E', borderRadius: 6, padding: '3px 10px', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.04em' },
  previewBox:  { marginBottom: 16, borderRadius: 9, overflow: 'hidden', background: '#f8fafc', minHeight: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid #e2e8f0' },
  media:       { width: '100%', maxHeight: 380, objectFit: 'contain', display: 'block' },
  caption:     { background: '#f9fafb', borderRadius: 8, padding: '12px 16px', fontSize: 13, lineHeight: 1.65, marginBottom: 16, color: '#334155', whiteSpace: 'pre-wrap', border: '1px solid #e2e8f0' },
  platformRow: { display: 'flex', alignItems: 'center', gap: 10, marginBottom: 18, flexWrap: 'wrap' },
  platBtn:     { padding: '7px 16px', borderRadius: 20, cursor: 'pointer', fontSize: 13, fontWeight: 600, transition: 'all .15s' },
  // Sticky to the bottom of the viewport so Approve/Delete stay reachable
  // without scrolling past the preview image + caption + platform chips
  // above — previously plain document flow, so a tall preview could push
  // these below the fold entirely on a normal-height screen.
  actions:     { display: 'flex', gap: 10, position: 'sticky', bottom: 0, background: '#fff', padding: '12px 4px', marginTop: 4, boxShadow: '0 -6px 16px rgba(0,0,0,.08)', zIndex: 10, borderRadius: '0 0 12px 12px' },
  btn:         { flex: 1, padding: '12px 0', borderRadius: 8, border: 'none', cursor: 'pointer', color: '#fff', fontSize: 14, fontWeight: 700 },
};
